import React, { useEffect, useRef, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { useGlobalState } from "./GlobalStateProvider";
import { fadeOutBGM, playBGM, playSE, stopBGM, toggleMute } from "./SoundEffect";
import DOMPurify from 'dompurify';
import "./Story.css";
import { GameUnlocks } from "./ModuleLinkFirebase";
import { app } from "./firebaseConfig";
import NazoEx from "./NazoEx";

const auth = getAuth(app);
const db = getFirestore(app);
const ORIGIN = "https://bad-assort.com/play/gothic-neue/origin.php";

type Data = {
  text?: string;
  image?: {
    src?: string;
    smooth?: boolean;
  };
  sound?: {
    type?: string,
    src?: string;
    stop?: boolean;
  }
  func?: string;
  next?: string;
  terminal?: string;
}[];

function Message({ currentText }: { currentText: string }) {
  const sanitizedText = DOMPurify.sanitize(currentText);
  return <div id="story-paragraph" key={currentText[0]} dangerouslySetInnerHTML={{ __html: sanitizedText }}></div>;
}

const Story: React.FC = () => {
  const { currentStep, setCurrentStep, logElements, setLogElements } = useGlobalState();

  const [user, setUser] = useState(auth.currentUser);
  const [firebaseDoc, setFirebaseDoc] = useState<GameUnlocks | null>(null);

  const [isStoryVisible, setStoryVisible] = useState<boolean>(true);
  const [isNazoVisible, setNazoVisible] = useState<boolean>(false);
  const [pass, setPass] = useState<string>("");
  const [data, setData] = useState<Data>([]);
  const [index, setIndex] = useState<number>(-1);
  const [currentText, setCurrentText] = useState<string>("");
  const [currentImage, setCurrentImage] = useState<string>("");
  const [BGClass, setBGClass] = useState<string>("");
  const [isMute, setMute] = useState<boolean>(false);

  const svgFilterModeRef = useRef<string | null>(null);
  const startAnimateRef = useRef<number>(0);

  useEffect(() => {
    if (!firebaseDoc) return;
    if (pass) {
      const email = firebaseDoc?.email;
      const udata = firebaseDoc?.activate;
      fetch(ORIGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pass: pass, uid: email, key: udata["Gothic:Neue"].code }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.json();
        })
        .then((newData) => {
          console.log(newData);
          if (newData["error"]) {
            alert(newData["error"]);
            return; //エラーハンドリング
          }
          setData(() => newData);
          setIndex(() => 0);
          setStoryVisible(true);
          setCurrentImage("");
        }).catch((error) => {
          console.error("Fetch error:", error);
        });
    } else {
      setStoryVisible(false);
      console.log("no pass");
    }
  }, [pass, firebaseDoc]);

  const handleClick = () => {
    const newIndex: number = index + 1 < data.length ? index + 1 : index
    if (data[newIndex].terminal) {
      setStoryVisible(false);
      switch (data[newIndex].terminal) {
        case "phase_101":
          setNazoVisible(true);
          break;
        case "ed_secret":
          setCurrentStep(103);
          fadeOutBGM(5);
          break;
        default:
          break;
      }
    } else if (data[newIndex].next) {
      refreshPass(data[newIndex].next ?? "");
    } else {
      setIndex(() => newIndex);
      setStoryVisible(true);
    }
    if (data[newIndex].func) {
      switch (data[newIndex].func) {
        case "clear":
          setBGClass("");
          break;
        default:
          svgFilterModeRef.current = data[newIndex].func ?? null;
          startAnimateRef.current = new Date().getTime();
          setBGClass(data[newIndex].func ?? "");
          break;
      }
    }
  };

  useEffect(() => {
    setCurrentText((currentText) => data[index]?.text ?? currentText);
    if (data[index]?.text) {
      let newArr = [...logElements];
      const txt: string = data[index].text ?? "";
      const sanitizedText = DOMPurify.sanitize(txt);
      newArr.push(<p key={newArr.length} dangerouslySetInnerHTML={{ __html: sanitizedText }}></p>);
      setLogElements(newArr);
    }
    if (data[index]?.image) {
      setCurrentImage(data[index].image?.src ?? "");
    }
    if (data[index]?.sound) {
      const soundSrc = data[index]?.sound?.src;
      const soundType = data[index]?.sound?.type ?? "BGM";
      if (soundSrc) {
        if (soundType === "SE") {
          stopBGM();
          playSE(soundSrc);
        } else if (soundType === "once") {
          playBGM(soundSrc, { loop: false });
        } else {
          playBGM(soundSrc, { loop: true });
        }
      } else if (data[index]?.sound?.stop) {
        fadeOutBGM(1);
      }
    }
  }, [index]);

  const refreshPass = (newPass: string) => {
    setPass(() => newPass);
    console.log(`Load new line: ${newPass}`);
  };

  useEffect(() => {
    switch (currentStep) {
      case 101:
      case 102:
        setNazoVisible(false);
        setPass(`story${currentStep}`);
        break;
    }
  }, [currentStep]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as GameUnlocks;
          setFirebaseDoc(userData);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  return <>
    <section id="bg-area">
      <img key={currentImage} src={`${process.env.PUBLIC_URL}/img/still_${currentImage}.webp`} className={BGClass} ></img>
    </section>
    {isStoryVisible &&
      <section id="story" onClick={handleClick}>
        <div id="story-back"></div>
        <Message currentText={currentText}></Message>
      </section>
    }
    {isNazoVisible &&
      <NazoEx></NazoEx>
    }

    <section id="utils">
      <div id="sound-button" onClick={() => { setMute(toggleMute()); }}>
        <img src={`${process.env.PUBLIC_URL}/img/icon-${isMute ? "mute" : "sound"}.png`} style={{ opacity: "0.4", filter: "invert(1)" }}></img>
      </div>
    </section>
  </>;
}

export default Story;
